// 机经题库列表父类列表

import type { settingData } from '@/api/common.api'
import {
  getUserTokenByPhoneApi,
  getWordUserInfoApi,
  type LexiconListBean
} from '@/views/word-recite/api'
import { http } from '@/utils'
import { saveWordToken } from '@/utils/token'
import { notifyCrossTab } from '@/utils/crossTabSync'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface defaultUserInfo extends Partial<UserInfo> {
  icon: string
  newMemberLevel: 0 | 1 | 2
  nonMemberDomains: NonMemberDomain[]
  /** 当前选择的词库（单词服务字段） */
  lexicon?: LexiconListBean
  /** 今日是否已打卡（单词服务字段） */
  isClock?: boolean
}

export const useUserStore = defineStore(
  'user',
  () => {
    const userInfo = ref<defaultUserInfo>({
      icon: new URL('/icon/default_190920.png', import.meta.url).href,
      newMemberLevel: 0,
      nonMemberDomains: []
    })

    /** 斩单词学习设置 */
    const learnSet = ref({
      autoPlay: true,
      accents: 0,
      autoNext: false,
      soundEffects: true
    })

    /** 防止并发请求导致 /api/auth/getUser 与 /api/auth/getUserTokenByPhone 被重复调用 */
    let getUserInfoPromise: Promise<defaultUserInfo | undefined> | null = null

    const getUserInfo = () => {
      if (getUserInfoPromise) return getUserInfoPromise

      getUserInfoPromise = http
        .get<defaultUserInfo>('/api/auth/getUser')
        .then((res) => {
          // 会员等级 / 到期时间发生变化（购买、兑换、续费、到期等）时，
          // 通知同域名下其他窗口刷新，同步会员数据
          const prevMemberLevel = userInfo.value.newMemberLevel ?? 0
          const prevMemberEndTime = userInfo.value.newMemberEndTime ?? 0
          userInfo.value = res.data
          if (
            prevMemberLevel !== (res.data.newMemberLevel ?? 0) ||
            prevMemberEndTime !== (res.data.newMemberEndTime ?? 0)
          ) {
            notifyCrossTab('memberUpdate')
          }
          // 换取单词服务登录态并拉取词库信息（失败不影响主流程）
          getUserTokenByPhoneApi()
            .then((tokenRes) => {
              if (tokenRes.data) {
                saveWordToken(tokenRes.data)
                return getWordUserInfoApi()
              }
            })
            .then((wordRes) => {
              if (wordRes?.data && userInfo.value) {
                userInfo.value.isClock = wordRes.data.isClock
                userInfo.value.lexicon = wordRes.data.lexicon
              }
            })
            .catch(() => undefined)
          return res.data
        })
        .catch(() => undefined)
        .finally(() => {
          getUserInfoPromise = null
        })

      return getUserInfoPromise
    }

    /** 未登录时本地保存的词库选择 */
    const localLexicon = ref<LexiconListBean>()
    /**
     * 是否为会员
     */
    const isMembers = computed(() => {
      if (userInfo.value && userInfo.value.newMemberLevel > 0) {
        return true
      }
      return false
    })

    /**
     * 是否有评测次数
     * @returns boolean
     */
    const isEnergy = () => {
      // 判断是否为会员
      if (isMembers.value) {
        return true
      }
      // 非会员判断是否有评测次数
      if (
        !userInfo.value ||
        !userInfo.value.nonMemberDomains ||
        !userInfo.value.nonMemberDomains[0] ||
        userInfo.value.nonMemberDomains[0]?.energy <= 0
      ) {
        // 判断是否为网校用户
        if (JSON.parse(window.localStorage.getItem('isZoom') || 'false')) {
          ElMessage.warning({
            message: '请联系相关老师开通VIP',
            duration: 3000
          })
          return false
        }
        // 没有用户信息
        ElMessage.warning({
          message: '剩余次数不够',
          duration: 3000
        })
        return false
      }
      return true
    }
    /** 修改头像，成功后同步更新本地 userInfo */
    const updateUserAvatar = async (icon: string) => {
      const res = await http.post<{ icon?: string }>('/api/user/updateImageAndNikeName', {
        icon
      })
      userInfo.value.icon = res.data?.icon || icon
      return userInfo.value
    }

    /** 修改昵称（昵称 + 密文成对提交），成功后同步更新本地 userInfo */
    const updateUserNickName = async (nickName: string, nickCipher: string) => {
      const res = await http.post<Partial<UserInfo>>('/api/user/updateImageAndNikeName', {
        nickName,
        nickCipher
      })
      userInfo.value.nickName = res.data?.nickName || nickName
      return userInfo.value
    }

    return {
      userInfo,
      localLexicon,
      learnSet,
      getUserInfo,
      isMembers,
      isEnergy,
      updateUserAvatar,
      updateUserNickName
    }
  },
  {
    persist: {
      storage: localStorage,
      paths: ['userInfo', 'localLexicon', 'learnSet']
    }
  }
)
export interface UserInfo {
  id: number
  uid: string
  icon: string
  nickName: string
  sex: number
  status: number
  language: string
  createTime: number
  tuoAge: number
  isMembers: number
  isOldMembers: number
  isTrialFree: number
  energy: number
  clockDay: number
  canClock: number
  vipBuyUrl: string
  isCollectInformation: boolean
  isCourseMember: number
  isCourseRefuel: number
  replacePhone: string
  userLevel: number
  nonMemberDomains?: NonMemberDomain[]
  newMemberLevel: number
  specialCode: number
  isedu: number
  showFreeMemberPopupsFlag: number
  hcadets: boolean
  courseDate: number
  newMemberEndTime: number
  bornDate?: string
  mobileToken: {
    expireIn: number
    token: string
  }
  userQuestionSetting: settingData
}

interface NonMemberDomain {
  type: number
  energy: number
  uid: string
}
